import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTicketRecurringIssuesReport,
  exportTicketRecurringIssuesReport,
  clearError,
} from "store/slices/reportSlice";
import {
  fetchInventoryCategories,
  fetchAllInventorySubcategories,
} from "store/slices/commonSlice";

import PageHeader from "components/common/PageHeader";
import { Card, CardHeader, CardTitle, CardContent } from "components/ui/card";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  AlertTriangle,
  Ticket,
  Hash,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import LogDateFilter from "components/common/LogDateFilter";
import DataTable from "components/common/DataTable";
import { Loader2 } from "lucide-react";
import { format } from "date-fns";
import { Input } from "components/ui/input";
import FormField from "components/common/FormField";
import CategorySubcategorySelect from "components/common/CategorySubcategorySelect";

const TicketRecurringIssuesReportPage = () => {
  const dispatch = useDispatch();

  const {
    ticketRecurringIssuesReport = {
      data: { summary: {}, results: [] },
      pagination: null,
    },
    isLoading,
    error,
  } = useSelector((state) => state.report);

  const summary = ticketRecurringIssuesReport.data.summary || {};
  const results = ticketRecurringIssuesReport.data.results || [];
  const pagination = ticketRecurringIssuesReport.pagination || {};

  // Advanced filter states
  const [advancedFilters, setAdvancedFilters] = useState({
    category: "all",
    sub_category: "all",
    issue_count_min: "",
    issue_count_max: "",
  });

  // Get categories and subcategories from Redux state
  const inventoryCategories = useSelector(
    (state) => state.common?.inventoryCategories?.items || []
  );
  const categoriesLoading = useSelector(
    (state) => state.common?.inventoryCategories?.loading || false
  );

  const subcategories = useSelector(
    (state) => state.common?.allInventorySubcategories.items || []
  );

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilters, setDateFilters] = useState({});
  const [isAdvancedFilterExpanded, setIsAdvancedFilterExpanded] =
    useState(false);

  // Calculate total pages from server response with fallback
  const totalPages = pagination.total_pages || 1;

  // Check if any advanced filters are active (excluding 'all' values)
  const hasActiveAdvancedFilters = Object.values(advancedFilters).some(
    (value) => value !== "" && value !== "all"
  );

  const refetch = useCallback(() => {
    // Build the API params object with all filters
    const apiParams = {
      page,
      page_size: pageSize,
      search: searchTerm,
      report_type: "recurring_issues",
      ...dateFilters,
      // Add advanced filters with proper naming for the backend
      ...(advancedFilters.category !== "all" && {
        category: advancedFilters.category,
      }),
      ...(advancedFilters.sub_category !== "all" && {
        sub_category: advancedFilters.sub_category,
      }),
      ...(advancedFilters.issue_count_min && {
        issue_count_min: advancedFilters.issue_count_min,
      }),
      ...(advancedFilters.issue_count_max && {
        issue_count_max: advancedFilters.issue_count_max,
      }),
    };


    dispatch(fetchTicketRecurringIssuesReport(apiParams));
  }, [dispatch, page, pageSize, searchTerm, dateFilters, advancedFilters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Reset to page 1 when search term, page size, or filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize, dateFilters, advancedFilters]);

  // Fetch all categories on component mount
  useEffect(() => {
    dispatch(fetchInventoryCategories());
    dispatch(fetchAllInventorySubcategories());
  }, [dispatch]);

  // --- Event Handlers ---
  const handleAdvancedFilterChange = (key, value) => {
    setAdvancedFilters((prev) => ({
      ...prev,
      [key]: value,
      // Reset subcategory when category changes
      ...(key === "category" && { sub_category: "all" }),
    }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      category: "all",
      sub_category: "all",
      issue_count_min: "",
      issue_count_max: "",
    });
  };

  const columns = [
    {
      accessorKey: "sub_category__category__name",
      header: "Category",
    },
    {
      accessorKey: "sub_category__name",
      header: "Sub Category / Issue",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.original.sub_category__name}</span>
        </div>
      ),
    },
    {
      accessorKey: "issue_count",
      header: "Occurrence Count",
      cell: ({ row }) => (
        <Badge variant="destructive" className="text-lg font-semibold">
          <AlertTriangle className="h-4 w-4 mr-1" />
          {row.original.issue_count}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Recurring Issues Report"
        description="Identify frequently reported issues by sub-category to prioritize fixes"
      />

      {/* <LogDateFilter onFilter={(f) => setDateFilters(f)} /> */}

      {/* Advanced Filters Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Advanced Filters
              {hasActiveAdvancedFilters && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </CardTitle>
            <div className="flex gap-2">
              {hasActiveAdvancedFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetAdvancedFilters}
                >
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setIsAdvancedFilterExpanded(!isAdvancedFilterExpanded)
                }
              >
                {isAdvancedFilterExpanded ? (
                  <>
                    Hide <ChevronUp className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Show <ChevronDown className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {isAdvancedFilterExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* <FormField
  label="Category"
  name="category"
  type="select"
  value={advancedFilters.category}
  onChange={(e) => handleAdvancedFilterChange('category', e.target.value)}
  disabled={categoriesLoading}
  options={[
    { value: 'all', label: 'All Categories' },
    ...(Array.isArray(inventoryCategories)
      ? inventoryCategories.map((cat) => ({
          value: String(cat.id),
          label: cat.name,
        }))
      : []),
  ]}
  placeholder={categoriesLoading ? "Loading..." : "Select category"}
/>

<FormField
  label="Sub Category"
  name="sub_category"
  type="select"
  value={advancedFilters.sub_category}
  onChange={(e) => handleAdvancedFilterChange('sub_category', e.target.value)}
  options={[
    { value: 'all', label: 'All Subcategories' },
    ...(Array.isArray(subcategories)
      ? subcategories.map((sub) => ({
          value: String(sub.id),
          label: sub.name,
        }))
      : []),
  ]}
  placeholder="Select subcategory"
/> */}

              <CategorySubcategorySelect
                category={advancedFilters.category}
                subCategory={advancedFilters.sub_category}
                onCategoryChange={(value) => {
                  setAdvancedFilters((prev) => ({
                    ...prev,
                    category: value,
                    sub_category: "all",
                  }));
                }}
                onSubCategoryChange={(value) =>
                  setAdvancedFilters((prev) => ({
                    ...prev,
                    sub_category: value,
                  }))
                }
              />

              <div className="space-y-2">
                <Label>Occurence Count Range</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Input
                      type="number"
                      placeholder="Min"
                      value={advancedFilters.issue_count_min}
                      onChange={(e) =>
                        handleAdvancedFilterChange(
                          "issue_count_min",
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      placeholder="Max"
                      value={advancedFilters.issue_count_max}
                      onChange={(e) =>
                        handleAdvancedFilterChange(
                          "issue_count_max",
                          e.target.value
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Issues Reported
            </CardTitle>
            <Ticket className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pagination.count || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Unique Issue Types
            </CardTitle>
            <Hash className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {summary.unique_subcategories || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Most Recurring Issue
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {summary.top_issue || "N/A"}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Avg Issues per Type
            </CardTitle>
            <Ticket className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary.unique_subcategories > 0
                ? (summary.total_issues / summary.unique_subcategories).toFixed(
                    1
                  )
                : 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* {isLoading && results.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin mr-3" />
          <span className="text-lg">Loading recurring issues data...</span>
        </div>
      ) : ( */}
      <DataTable
        data={results} // Use the paginated results from the server
        columns={columns}
        totalCount={pagination.count}
        searchPlaceholder="Search by category or sub-category..."
        onSearch={setSearchTerm}
        showSearch={true}
        pageIndex={page - 1} // Convert to 0-based index for DataTable
        pageSize={pageSize}
        pageCount={totalPages} // Use total pages from server response
        onPageChange={(p) => setPage(p + 1)} // Convert back to 1-based index
        onPageSizeChange={(newSize) => setPageSize(newSize)} // Page reset is handled by useEffect
        isLoading={isLoading}
        onExport={() =>
          dispatch(
            exportTicketRecurringIssuesReport({
              filters: {
                page,
                page_size: pageSize,
                search: searchTerm,
                report_type: "recurring_issues",
                ...dateFilters,
                // Include advanced filters in export
                ...(advancedFilters.category !== "all" && {
                  category: advancedFilters.category,
                }),
                ...(advancedFilters.sub_category !== "all" && {
                  sub_category: advancedFilters.sub_category,
                }),
                ...(advancedFilters.issue_count_min && {
                  issue_count_min: advancedFilters.issue_count_min,
                }),
                ...(advancedFilters.issue_count_max && {
                  issue_count_max: advancedFilters.issue_count_max,
                }),
              },
            })
          )
        }
        showExport={true}
        showImport={false}
      />
      {/* )} */}
    </div>
  );
};

export default TicketRecurringIssuesReportPage;
